import { useCallback, useEffect, useRef, useState } from 'react';
import { useListScrollCache } from '../Context/ListScrollCacheContext';

const PAGE_SIZE = 25;

function recordId(row) {
  return row?.Id || row?.id;
}

export function useInfiniteList({ cacheKey, fetchPage, resetKey, enabled = true }) {
  const { get, set } = useListScrollCache();
  const cached = get(cacheKey);
  const [records, setRecords] = useState(cached?.records || []);
  const [page, setPage] = useState(cached?.page || 1);
  const [totalCount, setTotalCount] = useState(cached?.totalCount || 0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);
  const prevReset = useRef(resetKey);
  const fetchLock = useRef(false);
  const lastPageShort = useRef(false);

  const hasMore = records.length < totalCount && !lastPageShort.current;

  const applyPage = useCallback((incoming, nextPage, append) => {
    const list = Array.isArray(incoming) ? incoming : (incoming?.records || incoming?.orders || incoming?.quotes || []);
    const count = Array.isArray(incoming) ? list.length : (incoming?.total_count ?? list.length);
    lastPageShort.current = list.length < PAGE_SIZE;
    setTotalCount(count);
    setPage(nextPage);
    setRecords((prev) => {
      if (!append) return list;
      const seen = new Set(prev.map(recordId));
      const extra = list.filter((row) => {
        const id = recordId(row);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      return [...prev, ...extra];
    });
  }, []);

  const load = useCallback(async ({ page: nextPage, append }) => {
    if (!enabled || fetchLock.current) return;
    fetchLock.current = true;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await fetchPage(nextPage, PAGE_SIZE);
      applyPage(data, nextPage, append);
    } catch (e) {
      setError(e);
      if (!append) {
        setRecords([]);
        setTotalCount(0);
      }
    } finally {
      fetchLock.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [enabled, fetchPage, applyPage]);

  useEffect(() => {
    if (prevReset.current !== resetKey) {
      prevReset.current = resetKey;
      lastPageShort.current = false;
      setRecords([]);
      setPage(1);
      setTotalCount(0);
      load({ page: 1, append: false });
      return;
    }
    if (!enabled) return;
    if (cached?.resetKey === resetKey && cached?.records?.length) return;
    load({ page: 1, append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, enabled]);

  useEffect(() => {
    set(cacheKey, { records, page, totalCount, resetKey, scrollY: window.scrollY });
  }, [cacheKey, records, page, totalCount, resetKey, set]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !enabled) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
        load({ page: page + 1, append: true });
      }
    }, { rootMargin: '240px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, hasMore, loading, loadingMore, page, load]);

  useEffect(() => {
    if (cached?.resetKey === resetKey && typeof cached.scrollY === 'number') {
      window.scrollTo(0, cached.scrollY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    records,
    totalCount,
    loading,
    loadingMore,
    error,
    hasMore,
    sentinelRef,
    pageSize: PAGE_SIZE,
    reload: () => load({ page: 1, append: false }),
  };
}

export { PAGE_SIZE };
